import { HubItemCategoryNodeKind } from "client/Api/Hub/common";
import { HubDataSource } from "client/Api/Hub/hub";
import {
	GroupHubItemSidebarNodeSaveModel,
	CategoryHubItemSidebarNodeSaveModel,
	GroupHubSidebarNodeDataSaveModel,
	CommunitiesRootHubSidebarNodeSaveModel,
	CommunityHubSidebarViewModel,
	HubHierarchySaveModel
} from "client/Api/Hub/hubSidebarManagement";
import { getClientId, ObjectWithClientId } from "client/Misc/clientId";
import { Guid } from "client_legacy/ndaw/guid";

export type HubItemGroupNodeFormModel = ObjectWithClientId<Omit<GroupHubItemSidebarNodeSaveModel, "groupData"> & {
	name: string;
	groupData: Omit<GroupHubSidebarNodeDataSaveModel, "children"> & {
		children: readonly HubItemChildNodeFormModel[];
	};
}>;

export type HubItemCategoryNodeFormModel = ObjectWithClientId<CategoryHubItemSidebarNodeSaveModel> & {
	name: string;
};

export type HubItemChildNodeFormModel =
	| HubItemGroupNodeFormModel
	| HubItemCategoryNodeFormModel;

export type BaseHubItemRootHubSidebarNodeFormModel = ObjectWithClientId<{
	children: readonly HubItemChildNodeFormModel[];
}>;

export type AnnouncementRootHubSidebarNodeFormModel = BaseHubItemRootHubSidebarNodeFormModel & {
	source: HubDataSource.Announcement;
};

export type EventRootHubSidebarNodeFormModel = BaseHubItemRootHubSidebarNodeFormModel & {
	source: HubDataSource.Event;
};

export type KnowledgeItemRootHubSidebarNodeFormModel = BaseHubItemRootHubSidebarNodeFormModel & {
	source: HubDataSource.KnowledgeItem;
};

export type CommunityChildNodeFormModel = ObjectWithClientId<CommunityHubSidebarViewModel>;

export type CommunitiesRootNodeFormModel = ObjectWithClientId<CommunitiesRootHubSidebarNodeSaveModel & {
	children: readonly CommunityChildNodeFormModel[];
}>;

export type HubItemRootNodeFormModel =
	| AnnouncementRootHubSidebarNodeFormModel
	| EventRootHubSidebarNodeFormModel
	| KnowledgeItemRootHubSidebarNodeFormModel;

export type FormModel = OverrideProperties<
	Omit<HubHierarchySaveModel, "sorting">,
	{
		sourcesInOrder: readonly HubDataSource[];
		announcementsRoot: OptionalModel<AnnouncementRootHubSidebarNodeFormModel>;
		communitiesRoot: OptionalModel<CommunitiesRootNodeFormModel>;
		eventsRoot: OptionalModel<EventRootHubSidebarNodeFormModel>;
		knowledgeItemsRoot: OptionalModel<KnowledgeItemRootHubSidebarNodeFormModel>;
	}
>;

export function getGroupsExcludingItself(
	groupId: Guid,
	items: readonly HubItemChildNodeFormModel[]
): readonly HubItemGroupNodeFormModel[] {
	return items
		.filter(
			x => getClientId(x) !== groupId
		)
		.filter((x) => x.kind === HubItemCategoryNodeKind.Group)
		.map(
			(x): HubItemGroupNodeFormModel => ({
				...x,
				groupData: {
					...x.groupData,
					children: getGroupsExcludingItself(
						groupId,
						x.groupData
							.children
					)
				}
			})
		);
}

export function excludeGroupButPromoteChildren(
	groupId: Guid,
	items: readonly HubItemChildNodeFormModel[]
): readonly HubItemGroupNodeFormModel[] {
	return items
		.filter((x) => x.kind === HubItemCategoryNodeKind.Group)
		.flatMap(
			item => {
				if (getClientId(item) === groupId) {
					return excludeGroupButPromoteChildren(groupId, item.groupData.children);
				}

				return [
					{
						...item,
						groupData: {
							...item.groupData,
							children: excludeGroupButPromoteChildren(groupId, item.groupData.children)
						}
					}
				];
			}
		);
}